/**
 * Shared currency formatting utilities.
 * Maps currency codes to symbols and formats prices/market caps accordingly.
 */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  JPY: '¥',
  KRW: '₩',
  CHF: 'CHF ',
  CAD: 'C$',
  AUD: 'A$',
  SEK: 'kr ',
  NOK: 'kr ',
  DKK: 'kr ',
  HKD: 'HK$',
  SGD: 'S$',
  NZD: 'NZ$',
  INR: '₹',
  CNY: '¥',
  TWD: 'NT$',
  MYR: 'RM ',
  THB: '฿',
  IDR: 'Rp ',
};

// Currencies that use 0 decimal places (prices are whole numbers)
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW']);

export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency?.toUpperCase()] || '$';
}

/**
 * Determine the number of decimal places for a currency.
 * JPY/KRW prices are whole numbers; others use 2 decimals.
 */
export function getCurrencyDecimals(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency?.toUpperCase()) ? 0 : 2;
}

/**
 * Format a price with the correct currency symbol and decimal places.
 * e.g. formatPrice(42.5, 'GBP') → '£42.50'
 *      formatPrice(3780, 'JPY') → '¥3,780'
 */
export function formatPrice(value: number | null | undefined, currency: string = 'USD'): string {
  if (value == null || isNaN(value)) return 'N/A';
  const symbol = getCurrencySymbol(currency);
  const decimals = getCurrencyDecimals(currency);
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${symbol}${formatted}`;
}

/**
 * Format a market cap with the correct currency symbol and magnitude suffix.
 * e.g. formatMarketCap(1.2e12, 'GBP') → '£1.2T'
 *      formatMarketCap(5.3e12, 'JPY') → '¥5.3T'
 */
export function formatMarketCap(value: number | null | undefined, currency: string = 'USD'): string {
  if (!value) return 'N/A';
  const symbol = getCurrencySymbol(currency);
  if (value >= 1e12) return `${symbol}${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `${symbol}${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `${symbol}${(value / 1e6).toFixed(1)}M`;
  return `${symbol}${value.toLocaleString()}`;
}

/**
 * Derive the currency code from a ticker suffix.
 * Used when no explicit currency field is available (e.g. Analytics, Portfolio).
 */
export function getCurrencyFromTicker(ticker: string): string {
  if (!ticker) return 'USD';
  if (ticker.endsWith('.T')) return 'JPY';
  if (ticker.endsWith('.KS')) return 'KRW';
  if (ticker.endsWith('.L')) return 'GBP';
  if (ticker.endsWith('.TO') || ticker.endsWith('.V') || ticker.endsWith('.CN') || ticker.endsWith('.NE')) return 'CAD';
  if (ticker.endsWith('.AX')) return 'AUD';
  if (ticker.endsWith('.HK')) return 'HKD';
  if (ticker.endsWith('.ST')) return 'SEK';
  if (ticker.endsWith('.OL')) return 'NOK';
  if (ticker.endsWith('.CO')) return 'DKK';
  if (ticker.endsWith('.SW') || ticker.endsWith('.VX')) return 'CHF';
  if (
    ticker.endsWith('.PA') || ticker.endsWith('.DE') || ticker.endsWith('.MC') ||
    ticker.endsWith('.AS') || ticker.endsWith('.BR') || ticker.endsWith('.MI') ||
    ticker.endsWith('.LS') || ticker.endsWith('.HE') || ticker.endsWith('.AT')
  ) return 'EUR';
  return 'USD';
}
